import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Container } from 'reactstrap';

import ContentPopups from '../../components/ad/ContentPopups';
import MainBanner from '../../components/ad/MainBanner';
import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import ScrollReset from '../../components/common/ScrollReset';
import AlternateLink from '../../components/head/AlternateLink';
import CanonicalLink from '../../components/head/CanonicalLink';
import RobotsMeta from '../../components/head/RobotsMeta';
import SEOTitle from '../../components/head/SEOTitle';
import GenericLayout from '../../components/layout/GenericLayout';
import Page from '../../components/layout/Page';
import MainProductList from '../../components/product-list/MainProductList';
import usePrepareBanner from '../../hooks/banner/usePrepareBanner';
import useShouldShowBanner from '../../hooks/banner/useShouldShowBanner';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AdStore from '../../store/AdStore';
import ConfigStore from '../../store/ConfigStore';
import SectionStore from '../../store/SectionStore';
import AdPages from '../../types/AdPages';
import AdZones from '../../types/AdZones';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import ProductSortOrderBy from '../../types/ProductSortOrderBy';
import SortOrder from '../../types/SortOrder';

const OnSalePage = ({
  adStore,
  configStore,
  sectionStore,
  routeService,
  intl,
}) => {
  const getBreadcrumbsPath = () => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.onSaleTitle),
        url: Paths.OnSale,
      },
    ]);
  };

  const getSearchParams = () => ({
    bannerPage: AdPages.CAMPAIGN_PRODUCTS,
  });

  usePrepareBanner({
    adStore,
    searchParameters: getSearchParams(),
    enabled: useShouldShowBanner({ adStore, page: AdPages.CAMPAIGN_PRODUCTS }),
  });

  const listId = 'on_sale_list';
  const listName = 'On Sale List';
  const fixedParams = {
    orderBy: configStore.siteConfig.isShoppingCenter
      ? ProductSortOrderBy.DISCOUNT
      : ProductSortOrderBy.CAMPAIGN_DATE_ADDED,
    order: SortOrder.DESC,
    hasCampaign: 1,
  };

  if (sectionStore.activeSection) {
    fixedParams.sections = [sectionStore.activeSection.id];
  }

  const getAdditionalContent = () => {
    return { robotsMeta: RobotsMeta };
  };

  const mainBanner = useShouldShowBanner({
    adStore,
    page: AdPages.CAMPAIGN_PRODUCTS,
    zone: AdZones.MAIN_BANNER,
  }) ? (
    <MainBanner searchParams={getSearchParams()} />
  ) : null;

  const mainBannerWithinContent =
    configStore.banner.mainBanner.withinContent &&
    !configStore.banner.mainBanner.fullWidth;

  return (
    <Page name={CommonPage.ON_SALE} className="OnSalePage">
      <ScrollReset />
      <SEOTitle
        title={{
          ...globalTranslations.onSaleTitle,
        }}
      />
      <CanonicalLink
        path={routeService.getPath(Paths.OnSale, sectionStore.activeSection)}
      />
      <AlternateLink path={Paths.OnSale} />
      {!mainBannerWithinContent && mainBanner}
      <Container className="OnSalePage__container">
        <GenericLayout
          bannerSearchParams={
            useShouldShowBanner({
              adStore,
              page: AdPages.CAMPAIGN_PRODUCTS,
              zone: AdZones.SIDEBAR_BANNER,
            })
              ? getSearchParams()
              : null
          }
          content={
            <>
              {mainBannerWithinContent && mainBanner}
              <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
              <section
                className="OnSalePage__main-content"
                aria-label={intl.formatMessage(
                  globalTranslations.pageContentRegionLabel
                )}
              >
                <PageTitle>
                  <FormattedMessage {...globalTranslations.onSaleTitle} />
                </PageTitle>
              </section>
              <MainProductList
                fixedParams={fixedParams}
                listId={listId}
                name={listName}
                showSort={true}
                additionalContent={getAdditionalContent()}
              />
              {useShouldShowBanner({
                adStore,
                page: AdPages.CAMPAIGN_PRODUCTS,
                zone: AdZones.POPUP_CONTENT,
              }) && <ContentPopups searchParams={getSearchParams()} />}
            </>
          }
        />
      </Container>
    </Page>
  );
};

OnSalePage.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject(
    'adStore',
    'configStore',
    'sectionStore',
    'routeService'
  )(observer(OnSalePage))
);
